import {
  HeaderMenuTitle,
  SearchListCard,
} from '@aibees/elephant/feature/search-list-card';
import { CustomPopover, Tab } from '@aibees/shared/components';
import { classnames } from '@aibees/shared/utils';
import { TabContext } from '@mui/lab';
import { Box, Card, CardContent } from '@mui/material';
import { memo, useState } from 'react';
import { RecordManagementHeader } from './components/record-management-header';
import { useRecordCounts } from './hooks/use-record-counts';
import { PopupParamsProvider } from './popup-params-provider';
import * as Styled from './styles';
import * as SX from './sx';
import type { ViewRecordsPopupProps as Props, RecordType } from './types';

const ANCHOR_ORIGIN = {
  vertical: 'bottom',
  horizontal: 'right',
} as const;
const TRANSFORM_ORIGIN = {
  vertical: 'top',
  horizontal: 'right',
} as const;

export const ViewRecordsPopup = memo(
  ({ className, state, type = 'lists' }: Props) => {
    const [value, setValue] = useState<RecordType>(type);

    const { exportsCount, labelsCount, listsCount, savedCount, enrichCount } =
      useRecordCounts();

    const handleChange = (
      event: React.SyntheticEvent,
      newValue: RecordType
    ) => {
      setValue(newValue);
    };

    return (
      <CustomPopover
        state={state}
        className={classnames('ViewRecordsPopup', className)}
        sx={SX.root}
        contentSx={SX.content}
        anchorOrigin={ANCHOR_ORIGIN}
        transformOrigin={TRANSFORM_ORIGIN}
      >
        <RecordManagementHeader value={value} />
        <Card variant="outlined">
          <CardContent>
            <TabContext value={value}>
              <Box>
                <Styled.Tabs
                  onChange={handleChange}
                  color="primary"
                  textColor="primary"
                  variant="fullWidth"
                  size="medium"
                >
                  <Tab
                    label="Lists"
                    value="lists"
                    iconPosition="end"
                    LabelContent={String(listsCount)}
                    LabelVariant="soft"
                  />
                  <Tab
                    label="Labels"
                    value="labels"
                    iconPosition="end"
                    LabelContent={String(labelsCount)}
                    LabelVariant="soft"
                  />
                  <Tab
                    label="Saved"
                    value="saved"
                    iconPosition="end"
                    LabelContent={String(savedCount)}
                    LabelVariant="soft"
                  />
                  <Tab
                    label="Exports"
                    value="exports"
                    iconPosition="end"
                    LabelContent={String(exportsCount)}
                    LabelVariant="soft"
                  />
                  <Tab
                    label="Enrichment"
                    value="enrich"
                    iconPosition="end"
                    LabelContent={String(enrichCount)}
                    LabelVariant="soft"
                  />
                </Styled.Tabs>
              </Box>

              <Styled.Panel value="lists">
                <PopupParamsProvider>
                  <SearchListCard title={HeaderMenuTitle.Lists} />
                </PopupParamsProvider>
              </Styled.Panel>
              <Styled.Panel value="labels">
                <PopupParamsProvider>
                  <SearchListCard title={HeaderMenuTitle.Labels} />
                </PopupParamsProvider>
              </Styled.Panel>
              <Styled.Panel value="saved">
                <PopupParamsProvider>
                  <SearchListCard title={HeaderMenuTitle.Saved} />
                </PopupParamsProvider>
              </Styled.Panel>
              <Styled.Panel value="exports">
                <PopupParamsProvider>
                  <SearchListCard title={HeaderMenuTitle.Export} />
                </PopupParamsProvider>
              </Styled.Panel>
              <Styled.Panel value="enrich">
                <PopupParamsProvider>
                  <SearchListCard title={HeaderMenuTitle.Enrich} />
                </PopupParamsProvider>
              </Styled.Panel>
            </TabContext>
          </CardContent>
        </Card>
      </CustomPopover>
    );
  }
);

ViewRecordsPopup.displayName = 'ViewRecordsPopup';
